import { Button, Input, InputWrapper } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import {
  bankersRoundToCents,
  formatCurrency,
  type Maybe,
} from '@vrplatform/utils';
import {
  Box,
  Group,
  NumberInput,
  Popover,
  SegmentedControl,
  Stack,
  type SegmentedControlItem,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  type AmountFilterNumberObject,
  formatAmountFilterToString,
  formatStringToAmountFilter,
} from '@vrplatform/ui-common';
import { type ReactNode, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useFeeForm } from '../useFeeForm';
import {
  FEE_AMOUNT_FILTER_DEFAULT_VALUE,
  getFeeAmountFilterLabel,
} from './FeeAmountFilterEditor.utils';

const FEE_AMOUNT_FILTER_OPERATOR_OPTIONS: SegmentedControlItem[] = [
  { label: 'Equal to', value: 'eq' },
  { label: 'Less than', value: 'lt' },
  { label: 'More than', value: 'gt' },
  { label: 'Between', value: 'between' },
];

const isEmptyAmount = (amount: AmountFilterNumberObject | null): boolean => {
  if (!amount) return true;

  if (amount.type !== 'between') {
    return typeof amount.centTotal !== 'number';
  }

  return (
    typeof amount.gtCentTotal !== 'number' ||
    typeof amount.ltCentTotal !== 'number'
  );
};

export const FeeAmountFilterEditor = () => {
  const methods = useFeeForm();
  const [opened, handlers] = useDisclosure(false);

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: theme.spacing.xs,
      })}
    >
      <Controller
        control={methods.control}
        name="accountTotalConditions"
        render={({ field, fieldState }) => {
          const displayValue = getFeeAmountFilterLabel(field.value);

          return (
            <Box
              miw={300}
              sx={{
                flex: 1,
              }}
            >
              <InputWrapper error={fieldState.error?.message}>
                <Popover
                  position="bottom-start"
                  shadow="md"
                  withinPortal
                  opened={opened}
                  onChange={(nextOpened) =>
                    nextOpened ? handlers.open() : handlers.close()
                  }
                >
                  <Popover.Target>
                    <Input
                      data-testid="fee-amount-filter-editor-input"
                      aria-label="Amount filter"
                      value={displayValue}
                      placeholder={FEE_AMOUNT_FILTER_DEFAULT_VALUE}
                      readOnly
                      onClick={handlers.toggle}
                      onKeyDown={(event) => {
                        if (
                          event.key === 'Enter' ||
                          event.key === ' ' ||
                          event.key === 'ArrowDown'
                        ) {
                          event.preventDefault();
                          handlers.toggle();
                        }
                      }}
                      rightSection={<Icon icon="ChevronIcon" size={16} />}
                      sx={{
                        input: {
                          cursor: 'pointer',
                        },
                      }}
                    />
                  </Popover.Target>
                  <Popover.Dropdown
                    data-dropdowntestid="fee-amount-filter-editor-input"
                    p={0}
                  >
                    <AmountFilterDropdown
                      value={field.value}
                      setValue={field.onChange}
                      closeModal={handlers.close}
                    />
                  </Popover.Dropdown>
                </Popover>
              </InputWrapper>
            </Box>
          );
        }}
      />
    </Box>
  );
};

const AmountFilterDropdown = ({
  value,
  setValue,
  closeModal: close,
}: {
  value: string | null | undefined;
  setValue: (value: string | null) => void;
  closeModal: () => void;
}) => {
  const methods = useForm<AmountFilterNumberObject>({
    values: value ? formatStringToAmountFilter(value) : undefined,
    defaultValues: {
      type: 'eq',
    },
  });

  const type = methods.watch('type');

  const closeModal = useCallback(
    (data?: AmountFilterNumberObject | null) => {
      close();
      methods.reset(
        data === undefined
          ? formatStringToAmountFilter(value)
          : (data ?? undefined)
      );
    },
    [close, methods, value]
  );

  const onSubmit = (amount: AmountFilterNumberObject) => {
    if (isEmptyAmount(amount)) {
      setValue(null);
      closeModal(null);
      return;
    }

    setValue(formatAmountFilterToString(amount));
    closeModal(amount);
  };

  const handleSubmit = methods.handleSubmit(onSubmit);

  return (
    <Stack sx={{ maxWidth: '320px' }} gap="xs" p="xs">
      <Controller
        name="type"
        control={methods.control}
        render={({ field }) => (
          <SegmentedControl
            data={FEE_AMOUNT_FILTER_OPERATOR_OPTIONS}
            size="xs"
            sx={{ width: '300px' }}
            {...field}
          />
        )}
      />

      {type !== 'between' ? (
        <Controller
          name="centTotal"
          control={methods.control}
          render={({ field, fieldState }) => (
            <AmountInput
              ariaLabel="Amount filter value"
              error={fieldState.error?.message}
              value={field.value ?? ''}
              name={field.name}
              onChange={(nextValue) =>
                field.onChange(
                  nextValue === undefined
                    ? null
                    : bankersRoundToCents(nextValue)
                )
              }
              handleSubmit={handleSubmit}
            />
          )}
        />
      ) : (
        <Group wrap="nowrap" gap="xs">
          <Controller
            name="gtCentTotal"
            control={methods.control}
            render={({ field, fieldState }) => (
              <AmountInput
                ariaLabel="Amount filter minimum"
                error={fieldState.error?.message}
                value={field.value ?? ''}
                name={field.name}
                onChange={(nextValue) =>
                  field.onChange(
                    nextValue === undefined
                      ? null
                      : bankersRoundToCents(nextValue)
                  )
                }
                handleSubmit={handleSubmit}
              />
            )}
          />
          <Controller
            name="ltCentTotal"
            control={methods.control}
            render={({ field, fieldState }) => (
              <AmountInput
                ariaLabel="Amount filter maximum"
                autoFocus={false}
                error={fieldState.error?.message}
                value={field.value ?? ''}
                name={field.name}
                onChange={(nextValue) =>
                  field.onChange(
                    nextValue === undefined
                      ? null
                      : bankersRoundToCents(nextValue)
                  )
                }
                handleSubmit={handleSubmit}
              />
            )}
          />
        </Group>
      )}

      <Group justify="flex-end">
        <Button size="xs" onClick={() => closeModal()}>
          Cancel
        </Button>
        <Button size="xs" variant="primary" onClick={handleSubmit}>
          Apply
        </Button>
      </Group>
    </Stack>
  );
};

const AmountInput = ({
  ariaLabel,
  value,
  onChange,
  error,
  name,
  autoFocus = true,
  handleSubmit,
}: {
  ariaLabel: string;
  value: Maybe<number | ''>;
  onChange: (value: number | undefined) => void;
  error: ReactNode;
  name: string;
  autoFocus?: boolean;
  handleSubmit: () => void;
}) => {
  return (
    <NumberInput
      aria-label={ariaLabel}
      value={typeof value === 'number' ? value / 100 : (value ?? '')}
      onChange={(nextValue) =>
        onChange(nextValue === '' ? undefined : Number(nextValue))
      }
      hideControls
      name={name}
      autoFocus={autoFocus}
      placeholder={formatCurrency(0)}
      decimalScale={2}
      thousandSeparator
      sx={(theme) => ({
        height: '100%',
        'input:focus': {
          boxShadow: `0px 0px 0px 2px ${
            theme.colors[error ? 'red' : theme.primaryColor][4]
          }40`,
        },
        'input:disabled': {
          color: 'unset',
        },
      })}
      error={error}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          handleSubmit();
        }
      }}
    />
  );
};
